/**
 * OrchistSearchFAB — Floating Action Button + fullscreen search overlay.
 *
 * Phase G.7.8 — JL: "searchbar for these Orchist lists app-wide. With
 * FAB capabilities!"
 *
 * The FAB lives bottom-right, fixed, on every page where Orchist lists
 * are visible. Tap → opens a contemplative fullscreen search overlay.
 * Doctrine-aligned: small circular paper-on-ink button with a sigil-
 * shaped magnifier, not a loud material-design "+".
 *
 * The overlay searches across:
 *   - the loaded lineage (currently-fetched pages)
 *   - the viewer's private nicknames + notes
 * Match algorithm: see matchesOrchistQuery in OrchistSearch.tsx.
 *
 * @scale-marker stage-a — overlay loads more from useLineage's cursor on
 *   demand if a search has matches but more pages exist server-side.
 *   Currently: client-filter on loaded rows only; the user can hit
 *   "load more" upstream to expand the search base.
 * @scale-marker stage-b — switch to server-side full-text via Postgres
 *   tsvector. The FAB then queries server directly per-keystroke (debounced).
 */

import { useEffect, useMemo, useState } from 'react';
import { useLineage } from '@/hooks/useLineage';
import { useNicknames } from '@/hooks/useNicknames';
import { useEmailShares } from '@/hooks/useEmailShares';
import { usePendingInvitees } from '@/hooks/usePendingInvitees';
import { useAuth } from '@/contexts/AuthContext';
import { resolveTimezone, utcToLocalDateString } from '@/lib/dates';
import {
  OrchistSearchInput,
  OrchistSearchResultRow,
  useOrchistSearchResults,
} from '@/components/OrchistSearch';
import { evaluateQuery, parseQuery } from '@/lib/fuzzy-search';

export interface OrchistSearchFABProps {
  /**
   * Whether to render the FAB at all. The FAB hides itself when not
   * applicable (e.g., empty lineage). Caller can override with `false`
   * on routes where the FAB is contextually wrong.
   */
  visible?: boolean;
}

export function OrchistSearchFAB({ visible = true }: OrchistSearchFABProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { contact } = useAuth();
  const viewerTz = resolveTimezone(contact?.timezone);

  const {
    rows,
    totalCount,
    hasMore,
    loadMore,
    loadingMore,
    loading: lineageLoading,
  } = useLineage();
  const { byTargetId } = useNicknames();
  const { byOwnerContactId: emailsByOwnerId } = useEmailShares();
  const { rows: pendingRows } = usePendingInvitees();

  const { results, matched, total } = useOrchistSearchResults(
    rows,
    byTargetId,
    query,
    emailsByOwnerId
  );

  // Pending invitees are part of the body of relationships even though
  // they're not yet in The Line publicly. If the viewer has nicknamed a
  // pending invitee at invite-time and searches for that nickname, the
  // overlay should surface them too — TAD-honest: the gesture exists.
  const pendingMatches = useMemo(() => {
    if (!query.trim()) return [];
    const parsed = parseQuery(query);
    return pendingRows.filter((p) => {
      const nick = byTargetId.get(p.id);
      const sent = utcToLocalDateString(p.invitationSentAt, viewerTz) ?? '';
      const haystack = [
        p.email,
        p.displayName ?? '',
        nick?.nickname ?? '',
        nick?.notes ?? '',
        sent,
        p.invitationAnonymous ? 'anonymous' : '',
      ]
        .join(' \n ')
        .toLowerCase();
      return evaluateQuery(haystack, parsed);
    });
  }, [pendingRows, query, byTargetId, viewerTz]);

  const noMatchesAtAll =
    !!query && results.length === 0 && pendingMatches.length === 0;

  // ESC closes overlay (not the FAB itself)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Reset query when overlay closes
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  if (!visible) return null;
  // FAB hides when there's nothing to search at all (no lineage AND no
  // pending invitations).
  if (!lineageLoading && rows.length === 0 && pendingRows.length === 0) return null;

  return (
    <>
      {/* The FAB itself */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search The Line"
        title="Search The Line"
        className="fixed bottom-6 right-6 z-20 w-12 h-12 rounded-full bg-paper border border-ink/30 shadow-lg flex items-center justify-center text-ink/70 hover:text-ink hover:border-ink/60 hover:shadow-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
      >
        <span aria-hidden className="text-base">⌕</span>
      </button>

      {/* The overlay */}
      {open && (
        <div className="fixed inset-0 z-30 flex items-start justify-center px-4 pt-12 pb-8 overflow-y-auto">
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close search"
            className="absolute inset-0 bg-paper/85 backdrop-blur-sm cursor-default"
          />

          <div className="relative w-full max-w-xl bg-paper border border-ink/20 shadow-xl p-6 md:p-8 space-y-5">
            <div className="text-center space-y-1">
              <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
                Search The Line
              </p>
              <h2 className="display text-2xl md:text-3xl font-medium leading-tight">
                <span className="italic text-accent">Find an Orchist.</span>
              </h2>
              <p className="text-xs text-ink/50 italic font-display">
                By public name, your private nickname, your notes, or
                acknowledgment date.
              </p>
            </div>

            <OrchistSearchInput
              value={query}
              onChange={setQuery}
              placeholder="who or when..."
              autoFocus
              summary={
                query
                  ? `${matched.toLocaleString()} of ${total.toLocaleString()} match`
                  : undefined
              }
            />

            {/* Results */}
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {noMatchesAtAll ? (
                <p className="text-sm italic text-ink/40 font-display text-center py-6">
                  No matches in the loaded set.
                  {hasMore && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => void loadMore()}
                        disabled={loadingMore}
                        className="not-italic underline underline-offset-4 decoration-ink/30 hover:decoration-ink/60 hover:text-ink disabled:opacity-50"
                      >
                        Load more from The Line
                      </button>{' '}
                      and try again.
                    </>
                  )}
                </p>
              ) : (
                <>
                  {results.length > 0 && (
                    <div className="space-y-2">
                      {results.slice(0, 50).map((row) => (
                        <OrchistSearchResultRow
                          key={row.id}
                          row={row}
                          nickname={byTargetId.get(row.id) ?? null}
                          emailShare={emailsByOwnerId.get(row.id) ?? null}
                          viewerTz={viewerTz}
                        />
                      ))}
                      {results.length > 50 && (
                        <p className="text-xs italic text-ink/40 font-display text-center py-2">
                          Showing first 50 matches. Refine the query to narrow.
                        </p>
                      )}
                    </div>
                  )}

                  {pendingMatches.length > 0 && (
                    <div className="space-y-2 pt-1">
                      <p className="text-[0.65rem] tracking-[0.2em] uppercase text-ink/45">
                        Pending invitations ({pendingMatches.length})
                      </p>
                      <ul className="space-y-2 list-none pl-0">
                        {pendingMatches.slice(0, 25).map((p) => {
                          const nick = byTargetId.get(p.id);
                          const sent = utcToLocalDateString(p.invitationSentAt, viewerTz);
                          return (
                            <li
                              key={p.id}
                              className="border-l-2 border-l-accent/30 bg-accent/5 px-3 py-2"
                            >
                              <p className="font-mono text-xs truncate">{p.email}</p>
                              {nick && (
                                <p className="text-xs text-accent italic font-display truncate">
                                  you call them:{' '}
                                  <span className="text-ink not-italic">{nick.nickname}</span>
                                </p>
                              )}
                              <p className="text-[0.6rem] tracking-[0.2em] uppercase text-ink/40 mt-0.5">
                                pending acknowledgment
                                {sent && <span className="ml-2 normal-case tracking-normal italic text-ink/45">· invited {sent}</span>}
                                {p.invitationAnonymous && (
                                  <span className="ml-2 normal-case tracking-normal italic text-accent">· sent anonymously</span>
                                )}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Load more — only when query is empty AND server has more */}
            {!query && hasMore && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="text-xs italic text-ink/55 underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink transition-colors disabled:opacity-50"
                >
                  {loadingMore
                    ? 'Loading more...'
                    : `Load more from The Line (${(totalCount ?? 0) - rows.length} remaining)`}
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
